"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { useContactModal } from "@/components/ContactModalProvider";
import { useRequestAudit } from "@/components/RequestAuditProvider";
import { platformServices } from "@/lib/platform-services";
import styles from "./HomeHero.module.css";

/* Same crisis CTA as the Incident Response page, kept in sync from one source. */
const incidentCta = platformServices["incident-response"].secondaryCta;

/*
  The homepage hero.

  The animated backdrop behind the copy is `hero-graphic/`. It is WebGL, so it
  needs a DOM to initialise and is loaded client-side only; until it paints,
  `.hero`'s static gradient stands in for it.

  The CTA opened the modal via a link to /request-audit that a document-level
  click handler intercepted; it is a button calling the hook directly now. The
  /request-audit route still exists for anyone who lands on it.
*/
const HeroGraphic = dynamic(() => import("./hero-graphic/HeroGraphic").then((mod) => mod.HeroGraphic), {
  ssr: false,
});

/*
  Whether to mount the graphic at all. It is the largest download on the site —
  a WebGL bundle plus a model and an environment map — and it is decorative, so
  it is skipped for anyone who has asked not to pay for that: Data Saver, or a
  reduced-motion preference, which the graphic's cursor tracking and animated
  gradient would both ignore.

  Starts false and is decided in an effect rather than during render: the mount
  is client-only already, and reading `matchMedia` while rendering would make
  the first client pass disagree with itself between the two.

  `.hero`'s static gradient is what shows in its place, the same fallback that
  covers the window before the graphic paints for everyone else.
*/
function useHeroGraphicEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setEnabled(!reducedMotion.matches);

    sync();
    reducedMotion.addEventListener("change", sync);

    return () => reducedMotion.removeEventListener("change", sync);
  }, []);

  return enabled;
}

export function HomeHero() {
  const { open } = useRequestAudit();
  const { open: openContact } = useContactModal();
  const graphicEnabled = useHeroGraphicEnabled();

  return (
    <div className={styles.hero}>
      <div className={styles.inner}>
        <div className={styles.banner}>
          {/*
            The eyebrow is a sibling of the gradient rule, not a child: the rule
            carries a mask-image, which would clip the text too.
          */}
          <div className={styles.tag}>
            <div className={styles.tagRule} aria-hidden="true" />
            <span className={styles.tagText}>Full Stack Web3 Security</span>
          </div>
          <h1 className={styles.title}>Securing the Future of Web3</h1>
          <p className={styles.lead}>
            Quantstamp is a global leader in blockchain security. Since 2017, we’ve secured billions in digital assets
            and worked with some of the most recognized names in web3.
          </p>
          <p className={styles.lead}>
            Whether it&apos;s smart contracts, L1s, or web infrastructure, we&apos;ve got you covered.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.cta} onClick={open}>
              <span>Request a Security Audit</span>
              <img src="/images/white-up-chevron.svg" alt="" aria-hidden="true" />
            </button>
            {incidentCta?.modal && (
              <button
                type="button"
                className={styles.crisisCta}
                onClick={() => openContact(incidentCta.modal!)}
              >
                <span className={styles.crisisDot} aria-hidden="true" />
                {incidentCta.label}
              </button>
            )}
          </div>
        </div>
      </div>
      {graphicEnabled && <HeroGraphic />}
    </div>
  );
}
